import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import { getUserInfo } from "../Authservice";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { resources } from "../Resourses/Resources";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#2C268A",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const AdministrationMinutes = () => {
  const [rows, setRows] = useState([]);
  const [meetingDate, setMeetingDate] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const user = getUserInfo()?.roles[0];

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState({
    url: "",
    name: "",
    type: "",
    blobUrl: null,
  });
  const fileInputRef = useRef(null);

  const fetchMinutes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${resources.APPLICATION_URL}getAllGoverningBodyMinutes`
      );
      setRows(response.data);
    } catch (error) {
      console.error("Error fetching minutes:", error);
      showSnackbar("Failed to fetch minutes", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setMeetingDate("");
    setSelectedFile(null);
    setEditingId(null);
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const validTypes = ["application/pdf"];
    if (file && validTypes.includes(file.type)) {
      setSelectedFile(file);
      setFileName(file.name);
    } else {
      showSnackbar("Please upload a PDF file only", "error");
    }
  };

  useEffect(() => {
    fetchMinutes();
    return () => {
      // Clean up blob URLs when component unmounts
      if (currentDocument.blobUrl) {
        URL.revokeObjectURL(currentDocument.blobUrl);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!meetingDate) {
      showSnackbar("Please enter meeting date", "error");
      return;
    }

    if (!selectedFile && !editingId) {
      showSnackbar("Please select a file", "error");
      return;
    }

    const newFormData = new FormData();
    if (selectedFile) {
      newFormData.append("file", selectedFile);
    }

    const jsonBlob = new Blob([JSON.stringify({ meetingDate })], {
      type: "application/json",
    });
    newFormData.append("minute", jsonBlob);

    try {
      setLoading(true);

      const url = editingId
        ? `${resources.APPLICATION_URL}saveGoverningBodyMinute?id=${editingId}`
        : `${resources.APPLICATION_URL}saveGoverningBodyMinute`;

      const response = await axios.post(url, newFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      showSnackbar(
        editingId
          ? "Minutes updated successfully!"
          : "Minutes added successfully!",
        "success"
      );
      resetForm();
      fetchMinutes();
    } catch (error) {
      console.error("Error saving minutes:", error);
      showSnackbar(
        error.response?.data?.message || "Failed to save minutes",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (minute) => {
    setMeetingDate(minute.meetingDate);
    setEditingId(minute.uniqueKey);
    setFileName(minute.fileName || "");
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(
        `${resources.APPLICATION_URL}deleteGoverningBodyMinutes?id=${id}`
      );
      showSnackbar("Document deleted successfully!", "success");
      setRows(rows.filter((row) => row.uniqueKey !== id));
    } catch (error) {
      console.error("Error deleting document:", error);
      showSnackbar(
        error.response?.data?.message || "Failed to delete document",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = async (documentPath, documentName) => {
    if (!documentPath) {
      showSnackbar("No document available", "error");
      return;
    }

    try {
      setLoading(true);

     
      const encodedPath = encodeURIComponent(documentPath);

      
      const response = await axios.get(
        `${resources.APPLICATION_URL}getFilePathtoConvertBase64?filePath=${encodedPath}`
      );

      
      const base64Data = response.data?.base64 || response.data;

      
      const byteCharacters = atob(base64Data);
      const byteNumbers = Array.from(byteCharacters).map((char) =>
        char.charCodeAt(0)
      );
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: "application/pdf" });

      
      const blobUrl = URL.createObjectURL(pdfBlob);

      
      if (currentDocument.blobUrl) {
        URL.revokeObjectURL(currentDocument.blobUrl);
      }

     
      setCurrentDocument((prev) => ({
        ...prev,
        url: blobUrl,
        name: documentName,
        type: "application/pdf",
        blobUrl: blobUrl,
      }));

      setViewDialogOpen(true);
    } catch (error) {
      console.error("Error preparing document for viewing:", error);
      showSnackbar("Failed to open document", "error");
    } finally {
      setLoading(false);
    }
  };


  const handleDownloadDocument = async () => {
    try {
      setLoading(true);
      const response = await axios.get(currentDocument.url, {
        responseType: "blob",
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", currentDocument.name || "document.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading document:", error);
      showSnackbar("Failed to download document", "error");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleCloseDialog = () => {
    setViewDialogOpen(false);
  };

  return (
    <div style={{ padding: "2rem" }}>
      <h1 style={{ marginBottom: "1rem" }}>GOVERNING BODY MINUTES</h1>
      <p style={{ marginBottom: "2rem" }}>
        The Governing Body shall guide the college while fulfilling the
        objectives for which the college has been granted an autonomous status.
      </p>

      <form
        onSubmit={handleSubmit}
        style={{
          marginBottom: "2rem",
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >

        {user === "ROLE_MANAGEMENT" &&
          <TextField
            label="Meeting Date"
            type="date"
            value={meetingDate}
            onChange={(e) => setMeetingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ width: 200 }}
            required
            inputProps={{
              max: new Date().toISOString().split("T")[0],
            }}
          />
        }
        {user === "ROLE_MANAGEMENT" &&
          <div style={{ position: "relative" }}>
            <Button variant="contained" component="label" style={{ width: 200 }}>
              {fileName ? "Change File" : "Choose File"}
              <input
                type="file"
                hidden
                onChange={handleFileUpload}
                accept=".pdf"
                required={!editingId}
                ref={fileInputRef}
              />
            </Button>

            {fileName && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: "4px",
                  fontSize: "0.75rem",
                  color: "#666",
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fileName}
              </div>
            )}
          </div>
        }
        {user === "ROLE_MANAGEMENT" &&
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {editingId ? "Update" : "Add"}
          </Button>
        }
        {editingId && (
          <Button onClick={resetForm} variant="outlined">
            Cancel
          </Button>
        )}
      </form>

      {loading && rows.length === 0 ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>S No</StyledTableCell>
                <StyledTableCell>Meeting Date</StyledTableCell>
                <StyledTableCell>Document</StyledTableCell>
                {user === "ROLE_MANAGEMENT" && <StyledTableCell>Actions</StyledTableCell>}              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <StyledTableRow key={row.uniqueKey}>
                  <StyledTableCell>{index + 1}</StyledTableCell>
                  <StyledTableCell>{row.meetingDate}</StyledTableCell>
                  <StyledTableCell>
                    <Button
                      onClick={() =>
                        handleViewDocument(row.documentPath, row.fileName)
                      }
                      disabled={!row.documentPath || loading}
                    >
                      View
                    </Button>
                  </StyledTableCell>
                  {user === "ROLE_MANAGEMENT" &&
                    <StyledTableCell>
                      <IconButton
                        onClick={() => handleEdit(row)}
                        disabled={loading}
                      >
                        <EditIcon style={{color:"#1976d2"}}/>
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(row.uniqueKey)}
                        disabled={loading}
                        color="error"
                      >
                        <DeleteIcon style={{color:"red"}}/>
                      </IconButton>
                    </StyledTableCell>
                  }
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        sx={{ height: "90vh" }}
      >
        <DialogTitle>
          {currentDocument.name || "Document Viewer"}
          <IconButton
            onClick={handleDownloadDocument}
            color="primary"
            sx={{ float: "right" }}
            disabled={loading}
          >
            <DownloadIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {currentDocument.url && (
            <Box sx={{ height: "70vh" }}>
              <embed
                src={currentDocument.url}
                type="application/pdf"
                width="100%"
                height="100%"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdministrationMinutes;
